import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useFetchCampaignData } from '../../../hooks/campaign/useFetchCampaignData.js';
import styles from './DeliveryReport.module.css';
import SummaryPage from '../SummaryPage/SummaryPage.jsx';
import CampaignDetails from '../CampaignDetails/CampaignDetails.jsx';
import CampaignPictures from '../CampaignPictures/CampaignPictures.jsx';
import CampaignExperience from '../CampaignExperience/CampaignExperience.jsx';
import QueryStringService from '../../../services/queryStringService.js';
import HashParameterService from '../../../services/hashParameterService.js';
import ExportService from '../../../services/exportService.js';
import { useLoading } from '../Contexts/LoadingContext';
import { useError } from '../Contexts/ErrorContext';
import SubMenu from '../SubMenu/SubMenu.jsx';
import PanelMap from '../Map/Map.jsx';
import AddressList from '../AddressList/AddressList.jsx';
import OutdootImpact from '../OutdoorImpact/OutdoorImpact.jsx';
import CampaignEffectMeasure from '../CampaignEffectMeasure/CampaignEffectMeasure.jsx';
import campaignValues from '../../../utils/campaignValues.js';
import dateFunctions from '../../../utils/dateFunctions.js';
import AddTopLevelIconAndText from '../HigherOrderComponents/AddTopLevelIconAndText/AddTopLevelIconAndText.jsx';
import { ExportTypes } from '../Managers/ExportManager';
import {
    initializeHeadlessMap,
    getMapInstance,
    hasMapInstance
} from '../Map/mapService.js';
import {
    CAMPAIGN_STATUS,
    PRODUCT_TYPES,
    CAMPAING_PROMISE_TYPES,
    COUNTRY_CODES,
    CLASSIC_PANEL_STATUS,
    CREATIVE_MIMES,
    AD_TYPES
} from '../../../constants/enums';

// Create enhanced components with export functionality
const EnhancedPanelMap = AddTopLevelIconAndText(props => {
    return <PanelMap {...props} />;
});

const EnhancedCampaignDetails = AddTopLevelIconAndText(CampaignDetails);
const EnhancedSummaryPage = AddTopLevelIconAndText(SummaryPage);
const EnhancedAddressList = AddTopLevelIconAndText(AddressList);
const EnhancedCampaignPictures = AddTopLevelIconAndText(CampaignPictures);
const EnhancedCampaignExperience = AddTopLevelIconAndText(CampaignExperience);
const EnhancedOutdootImpact = AddTopLevelIconAndText(OutdootImpact);
const EnhancedCampaignEffectMeasure = AddTopLevelIconAndText(CampaignEffectMeasure);

const CAMPAIGN_EXPERIENCE_VIDEO_MIMES = new Set([
    CREATIVE_MIMES.MP4,
    CREATIVE_MIMES.MOV
]);

const CAMPAIGN_EXPERIENCE_IMAGE_MIMES = new Set([
    CREATIVE_MIMES.JPG,
    CREATIVE_MIMES.PNG,
    CREATIVE_MIMES.JPEG,
    CREATIVE_MIMES.GIF,
    CREATIVE_MIMES.WEBP
]);

const normalizeCampaignExperienceMime = (mime) => {
    const normalizedMime = mime?.toLowerCase().trim() || '';

    if (!normalizedMime.includes('/')) {
        return normalizedMime;
    }

    return normalizedMime.split('/').pop() || '';
};

const isCampaignExperienceMediaMime = (mime) => {
    const normalizedMime = normalizeCampaignExperienceMime(mime);

    return CAMPAIGN_EXPERIENCE_VIDEO_MIMES.has(normalizedMime) ||
        CAMPAIGN_EXPERIENCE_IMAGE_MIMES.has(normalizedMime);
};

const getCampaignExperienceMediaType = (mime) => {
    const normalizedMime = normalizeCampaignExperienceMime(mime);

    return CAMPAIGN_EXPERIENCE_VIDEO_MIMES.has(normalizedMime)
        ? AD_TYPES.VIDEO
        : AD_TYPES.IMAGE;
};

const getCampaignExperienceStoredAs = (campaignExperience) => {
    return campaignExperience?.replacedStoredAs ||
        campaignExperience?.storedAs ||
        '';
};

const getCampaignExperienceFilename = (campaignExperience) => {
    return campaignExperience?.replacedFilename ||
        campaignExperience?.originalFilename ||
        '';
};

const getCampaignExperienceMime = (campaignExperience) => {
    return campaignExperience?.replacedMime ||
        campaignExperience?.mime ||
        '';
};

const DeliveryReport = ({
    orderData,
    customerId,
    parameters,
    campaignType,
    activePage,
    setActivePage,
    orderlinesData: externalOrderlinesData,
    setOrderlinesData: setExternalOrderlinesData,
    campaignPromiseType,
    campaignName

}) => {
    const { setIsLoading } = useLoading();
    const { showError } = useError();
    const queryStringService = useMemo(() => new QueryStringService(), []);
    const hashParamService = useMemo(() => new HashParameterService(), []);
    const [mapInitialized, setMapInitialized] = useState(false);
    const [effectMeasureEnabled, seteffectMeasureEnabled] = useState(false);

    const initialOrderlineIds = useMemo(() => {
        const orderlinesFromUrl = hashParamService.getParameter('orderlines');
        return orderlinesFromUrl ? orderlinesFromUrl.split(',').map(Number) : null;
    }, [hashParamService]);

    const [selectedOrderlineIds, setSelectedOrderlineIds] = useState(initialOrderlineIds);
    const [orderlinesMetaData, setOrderlinesMetaData] = useState(null);
    const [campaignStatus, setCampaignStatus] = useState(CAMPAIGN_STATUS.NOT_STARTED);
    const [daysUntilStart, setDaysUntilStart] = useState(0);
    const [subMenuConditions, setSubMenuConditions] = useState({
        OIEnabled: false,
        DetailsEnabled: true,
        PicturesEnabled: false,
        CampaignExperienceEnabled: false
    });

    const countryCode = useMemo(() => queryStringService.getCurrentCountry(), [queryStringService]);
    const showClientOrderNumberTextBasedOnCountry = useMemo(() =>
        countryCode === COUNTRY_CODES.DENMARK, [countryCode]
    );
    const productPromiseDescription = useMemo(() =>
        campaignValues.fetchPromiseUserFriendlyDescriptionBasedOnCampaignPromise(campaignPromiseType),
        [campaignPromiseType]
    );

    const selectedOrderline = useMemo(() => {
        if (!selectedOrderlineIds || !orderData?.orderLines) return null;
        return orderData.orderLines.find(ol =>
            selectedOrderlineIds.includes(Number(ol.id))
        );
    }, [selectedOrderlineIds, orderData?.orderLines]);

    const { loading, data: fetchedOrderlinesData, error } = useFetchCampaignData(
        selectedOrderline,
        customerId,
        countryCode
    );

    useEffect(() => {
        if (!selectedOrderline) {
            hashParamService.removeParameter('orderlines');
            return;
        }
        if (selectedOrderline.effectMeasure) {
            seteffectMeasureEnabled(true);
        }
    }, [selectedOrderline, hashParamService]);

    // Process panels for address list export
    const validPanels = useMemo(() => {
        if (!externalOrderlinesData) return [];

        if (campaignType === PRODUCT_TYPES.PLAY) {
            return externalOrderlinesData?.playData?.contacts?.panels || [];
        } else {
            return externalOrderlinesData?.postingData || [];
        }
    }, [externalOrderlinesData, campaignType]);

    // Function to transform panels data for map component
    const transformPanelsToMapComponent = useCallback((panelData) => {
        const panelMap = new Map();

        if (panelData && panelData.length > 0) {
            panelData.forEach(group => {
                if (group.hasOwnProperty("panels")) {
                    group.panels.forEach(panel => {
                        if (!panelMap.has(panel.panelId)) {
                            panelMap.set(panel.panelId, {
                                latitude: panel.latitude,
                                longitude: panel.longitude,
                                panelId: panel.panelId,
                                address: panel.address,
                                city: panel.city,
                                panelIdsAtLocation: panel.panelIdsAtLocation,
                                statusInformation: new Set()
                            });
                        }
                        const statusInfo = JSON.stringify({
                            status: panel.status.toLowerCase() === CLASSIC_PANEL_STATUS.LATE ? 'Posted late' : panel.status,
                            color: panel.color,
                            incidents: panel.incidents,
                            timeStamp: panel.timeStamp,
                            design: panel.design
                        });

                        panelMap.get(panel.panelId).statusInformation.add(statusInfo);
                    });
                }
            });
        }

        // Convert Set back to array and parse JSON strings as we don't want any duplicates
        for (let panel of panelMap.values()) {
            panel.statusInformation = Array.from(panel.statusInformation).map(str => JSON.parse(str));
        }
        return Array.from(panelMap.values());
    }, []);

    useEffect(() => {
        const initializeMap = async () => {
            setIsLoading(true);

            try {
                if (!orderData?.orderId || !customerId) {
                    setMapInitialized(false);
                    setIsLoading(false);
                    return;
                }

                const mapPanels = campaignType === PRODUCT_TYPES.PLAY
                    ? transformPanelsToMapComponent([externalOrderlinesData?.playData?.plays ?? []])
                    : transformPanelsToMapComponent(externalOrderlinesData?.postingData ?? []);

                if (mapPanels.length > 0) {
                    // Check if we already have the correct instance for this order/customer and orderline
                    const hasCorrectInstance = hasMapInstance(orderData.orderId, customerId, selectedOrderlineIds.join(","));

                    // Always reinitialize if the order/customer changed
                    await initializeHeadlessMap(
                        orderData.orderId,
                        customerId,
                        selectedOrderlineIds.join(","),
                        campaignType,
                        orderData.customer,
                        campaignName,
                        mapPanels,
                        !hasCorrectInstance // Force reinitialization if IDs don't match
                    );

                    campaignName 

                    // Mark initialization as complete
                    setMapInitialized(true);
                } else {
                    // No panels available, but we've completed the initialization attempt
                    setMapInitialized(true);
                }
            } catch (error) {
                console.error('Failed to initialize headless map:', error);
                showError('Failed to initialize map: ' + error.message);
                // Even though there was an error, we consider initialization complete
                setMapInitialized(true);
            } finally {
                setIsLoading(false);
            }
        };

        // Reset map initialized state when order/customer changes
        setMapInitialized(false);

        // Always try to initialize when order data or customer ID changes
        if (externalOrderlinesData) {
            initializeMap();
        }
    }, [orderData?.orderId, customerId, campaignType, externalOrderlinesData, transformPanelsToMapComponent, showError, setIsLoading]);

    // Centralized panel pictures preparation 
    const panelPictures = useMemo(() => {
        if (!externalOrderlinesData?.campaignUploads?.panelPictures) return [];

        return externalOrderlinesData.campaignUploads.panelPictures.map(pict => ({
            ...pict,
            storedAs: parameters.panelPicturesUrl + pict.storedAs,
            orgFileName: pict.orgFileName || `panel_picture_${pict.id}.jpg`
        }));
    }, [externalOrderlinesData, parameters]);

    const campaignExperienceMedia = useMemo(() => {
        const externalCreatives = externalOrderlinesData?.campaignUploads?.campaignExperience || [];

        return externalCreatives
            .filter(creative => isCampaignExperienceMediaMime(getCampaignExperienceMime(creative)))
            .map(creative => {
                const normalizedMime = getCampaignExperienceMime(creative)?.toLowerCase() || '';
                const storedAs = getCampaignExperienceStoredAs(creative);
                const filename = getCampaignExperienceFilename(creative);
                const mediaUrl = `${parameters.campaignExperienceUrl}${storedAs}`;

                return {
                    ...creative,
                    alt: filename || `Campaign experience ${creative.id || creative.UploadedAt}`,
                    filename: filename || `creative_${creative.id || creative.UploadedAt}.${normalizedMime || CREATIVE_MIMES.JPG}`,
                    mime: normalizedMime,
                    storedAs,
                    type: getCampaignExperienceMediaType(normalizedMime),
                    src: mediaUrl,
                    url: mediaUrl
                };
            });
    }, [externalOrderlinesData, parameters]);

    // Generated campaign name for display and export
    const campaignDisplayName = useMemo(() => {
        if (!orderlinesMetaData || !orderlinesMetaData[0]) return '';

        return campaignValues.caluclateCampaignName(
            orderlinesMetaData[0].alternativeName,
            orderData.customer,
            orderlinesMetaData[0].periodStart,
            orderlinesMetaData[0].periodEnd
        );
    }, [orderlinesMetaData, orderData]);

    // Function to fetch media array for creative export
    const fetchMediaArrayForCreativeExport = useCallback(() => {
        if (!externalOrderlinesData || !parameters) return [];

        const externalMediaForExport = externalOrderlinesData?.campaignUploads?.externalCreatives || [];
        const creativeInfoForExport = externalMediaForExport.map(creative => ({
            filename: creative.orgFileName || `creative_${creative.id}.${creative.mime || 'jpg'}`,
            url: `${parameters.externalCreativeUrl}${creative.storedAs}`
        }));

        const mediaForExport = externalOrderlinesData?.orderLineDataInformation?.creatives || [];
        const combinedCreativeInfoForExport = creativeInfoForExport.concat(mediaForExport.map(creative => ({
            filename: creative.name || `creative_${creative.id}.${creative.mime}`,
            url: `${parameters.creativeImageUrl}${creative.id}.${creative.mime}`
        })));

        return combinedCreativeInfoForExport;
    }, [externalOrderlinesData, parameters]);

    // Create a campaign effect export handler using campaign name directly calculated from metadata
    const createCampaignEffectExportHandler = useCallback(() => {
        return () => {
            try {
                // Calculate campaign name inside the handler to avoid circular references
                const effectCampaignName = orderlinesMetaData && orderlinesMetaData[0] ?
                    campaignValues.caluclateCampaignName(
                        orderlinesMetaData[0].alternativeName,
                        orderData.customer,
                        orderlinesMetaData[0].periodStart,
                        orderlinesMetaData[0].periodEnd
                    ) : 'Campaign';

                const campaignData = {
                    title: `Campaign Effect - ${effectCampaignName}`,
                    orderlineId: selectedOrderlineIds?.[0],
                    data: externalOrderlinesData?.outdoorImpactData || {}
                };

                return ExportService.exportCampaignEffectData(
                    `CampaignEffect_${effectCampaignName}`,
                    campaignData
                ).catch(error => {
                    console.error('Failed to export campaign effect data:', error);
                    showError('Failed to export campaign effect data: ' + error.message);
                    throw error;
                });
            } catch (error) {
                console.error('Error in campaign effect export:', error);
                showError('Failed to export campaign effect measure: ' + error.message);
                throw error;
            }
        };
    }, [orderlinesMetaData, orderData, selectedOrderlineIds, externalOrderlinesData, showError]);

    // Generate shared props for all components
    const getSharedExportProps = useCallback(() => {
        const mediaArray = fetchMediaArrayForCreativeExport();

        // Only access map instance if it's been properly initialized
        const mapInstance = mapInitialized ? getMapInstance() : null;

        return {
            // Common props for all views
            mediaArray,
            campaignName: campaignDisplayName,
            orderData,
            customerId,
            campaignType,
            parameters,
            selectedOrderlineMetaData: orderlinesMetaData,
            allowAddressExport: true,
            exportPanelSOVValue: parameters.clientSettings?.exportPanelSOVValue,
            exportPanelVACValue: campaignValues.showAddressListVACDeliveredValue(campaignPromiseType),
            panels: validPanels,
            panelPictures,
            onExportCampaignEffect: createCampaignEffectExportHandler(),
            _mapInstance: mapInstance,
            campaignPromiseType: campaignPromiseType,
            // HOC-specific props
            showImportCampaignDateText: false,
            showClientOrderNumberText: false,
            hocCampaignStatus: campaignStatus,
            productPromiseDescription,
            campaignStartsInDays: daysUntilStart,

            disabledExports: [
                ...(mapInitialized ? [] : [ExportTypes.MAP_URL]),
                ...(effectMeasureEnabled ? [] : [ExportTypes.CAMPAIGN_EFFECT])
            ]};
    }, [
        fetchMediaArrayForCreativeExport,
        campaignDisplayName,
        orderData,
        customerId,
        campaignType,
        parameters,
        orderlinesMetaData,
        validPanels,
        panelPictures,
        createCampaignEffectExportHandler,
        campaignStatus,
        productPromiseDescription,
        daysUntilStart,
        mapInitialized 
    ]);

    useEffect(() => {
        setIsLoading(loading);
    }, [loading, setIsLoading]);

    useEffect(() => {
        if (error) {
            console.error("Error fetching campaign data:", error);
            showError("Error loading campaign data. Please try again.");
        }
    }, [error, showError]);

    useEffect(() => {
        if (!selectedOrderlineIds || !orderData?.orderLines) return;

        const orderlines = orderData.orderLines.filter(ol =>
            selectedOrderlineIds.includes(Number(ol.id))
        );
        setOrderlinesMetaData(orderlines);

        if (orderlines.length > 0) {
            const metadata = orderlines[0];
            const calculatedStatus = campaignValues.campaignStatus(
                metadata.periodStart,
                metadata.periodEnd
            );
            setCampaignStatus(calculatedStatus);
            setDaysUntilStart(calculatedStatus === CAMPAIGN_STATUS.NOT_STARTED
                ? Math.abs(dateFunctions.daysBetweenDates(
                    metadata.periodStart,
                    dateFunctions.getISOFormatFromDate(new Date())
                ))
                : 0);
        }
    }, [selectedOrderlineIds, orderData?.orderLines]);

    useEffect(() => {
        if (!fetchedOrderlinesData) return;

        setExternalOrderlinesData(fetchedOrderlinesData);

        // For SCV campaigns, re-evaluate status once fetched data is available,
        // as COMPLETED requires the outdoor impact file to be present.
        if (campaignPromiseType === CAMPAING_PROMISE_TYPES.SE_CLASSIC_VAC && orderlinesMetaData?.[0]) {
            const metadata = orderlinesMetaData[0];
            const idsExportFileExists = fetchedOrderlinesData.orderLineDataInformation?.idsExportFileExists ?? null;
            const recalculatedStatus = campaignValues.campaignStatus(
                metadata.periodStart,
                metadata.periodEnd,
                metadata.localParameter5,
                idsExportFileExists
            );
            setCampaignStatus(recalculatedStatus);
        }

        const showOITab = [
            CAMPAING_PROMISE_TYPES.SE_CLASSIC_EAVAC,
            CAMPAING_PROMISE_TYPES.SE_DIGITAL_VAC,
            CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_OI,
            CAMPAING_PROMISE_TYPES.SE_CLASSIC_VAC,
            CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_EAVAC_OI

        ].includes(campaignPromiseType);

        setSubMenuConditions({
            OIEnabled: showOITab,
            DetailsEnabled: !campaignValues.isNonMOMSFollowUpCampaign(campaignPromiseType),
            PicturesEnabled: (fetchedOrderlinesData?.campaignUploads?.panelPictures || []).length > 0,
            CampaignExperienceEnabled: (fetchedOrderlinesData?.campaignUploads?.campaignExperience || [])
                .some(creative => isCampaignExperienceMediaMime(getCampaignExperienceMime(creative))),
            EffectMeasureEnabled: selectedOrderline.effectMeasure ? true : false
        });
    }, [fetchedOrderlinesData, campaignPromiseType, orderlinesMetaData, setExternalOrderlinesData]);

    // Render the appropriate view based on the active page
    const renderPage = useCallback(() => {
        if (!externalOrderlinesData) return null;

        // For Map view, wait until map is initialized
        if (activePage === 'Map' && !mapInitialized) {
            return (
                <div className={styles.loadingContainer}>
                    <p>Initializing map data...</p>
                </div>
            );
        }

        // Fetch base shared props
        const sharedProps = getSharedExportProps();

        switch (activePage) {
            case 'Map': {
                const mapPanels = campaignType === PRODUCT_TYPES.PLAY
                    ? transformPanelsToMapComponent([externalOrderlinesData?.playData?.plays ?? []])
                    : transformPanelsToMapComponent(externalOrderlinesData?.postingData ?? []);

                return (
                    <EnhancedPanelMap
                        {...sharedProps}
                        panels={mapPanels}
                        orderId={orderData.orderId}
                        customerId={customerId}
                        orderlineIds={selectedOrderlineIds.join(",")}
                        campaignType={campaignType}
                        header="Panel locations"
                        showPanelExport={false}
                        showButtonBorder={true}
                    />
                );
            }

            case 'Details': {
                return (
                    <EnhancedCampaignDetails
                        {...sharedProps}
                        campaignPromiseType={campaignPromiseType}
                        selectedOrderlineIds={selectedOrderlineIds}
                        orderlinesData={externalOrderlinesData}
                        clientOrderNumber={orderlinesMetaData?.[0]?.clientOrderNumber ?? ""}
                        showImportCampaignDateText={true}
                        importCampaignDate={dateFunctions.getCountrySpecificDateTimeFormatFromDate(
                            countryCode,
                            externalOrderlinesData?.orderLineDataInformation?.latestUpdateDate
                        ) ?? ""}
                    />
                );
            }

            case 'AddressList': {
                return (
                    <EnhancedAddressList
                        {...sharedProps}
                        orderlinesData={externalOrderlinesData}
                        campaignPromiseType={campaignPromiseType}
                        showImportCampaignDateText={true}
                        importCampaignDate={dateFunctions.getCountrySpecificDateTimeFormatFromDate(
                            countryCode,
                            externalOrderlinesData?.orderLineDataInformation?.latestUpdateDate
                        ) ?? ""}
                    />
                );
            }

            case 'OutdoorImpact': {
                const outdoorImpactProps = {
                    ...sharedProps,
                    setIsLoading,
                    orderlinesData: externalOrderlinesData,
                    selectedOrderlineIds,
                    targetSoV: orderlinesMetaData?.[0]?.targetSoV ?? 0,
                    campaignPromiseType: campaignPromiseType,
                    campaignStatus: campaignStatus
                };

                return <EnhancedOutdootImpact {...outdoorImpactProps} />;
            }

            case 'Pictures': {
                return (
                    <EnhancedCampaignPictures
                        {...sharedProps}
                    />
                );
            }

            case 'CampaignExperience': {
                return (
                    <EnhancedCampaignExperience
                        {...sharedProps}
                        mediaArray={campaignExperienceMedia}
                        campaignExperienceMedia={campaignExperienceMedia}
                        disabledExports={[
                            ...(sharedProps.disabledExports || []),
                            ExportTypes.PICTURES,
                            ExportTypes.CREATIVES
                        ]}
                    />
                );
            }

            case 'EffectMeasure': {
                return (
                    <EnhancedCampaignEffectMeasure
                        {...sharedProps}
                    />
                );
            }

            case 'Summary':
            default: {
                return (
                    <EnhancedSummaryPage
                        {...sharedProps}
                        orderlinesData={externalOrderlinesData}
                        playSuccessLimit={parameters.playSuccessLimit}
                        postingSuccessLimit={parameters.postingSuccessLimit}
                        campaignPromiseType={campaignPromiseType}
                        showClientOrderNumberText={showClientOrderNumberTextBasedOnCountry}
                        clientOrderNumber={orderlinesMetaData?.[0]?.clientOrderNumber ?? ""}
                        showImportCampaignDateText={true}
                        importCampaignDate={dateFunctions.getCountrySpecificDateTimeFormatFromDate(
                            countryCode,
                            externalOrderlinesData?.orderLineDataInformation?.latestUpdateDate
                        ) ?? ""}
                    />
                );
            }
        }
    }, [
        externalOrderlinesData,
        getSharedExportProps,
        activePage,
        campaignType,
        transformPanelsToMapComponent,
        orderData,
        customerId,
        campaignPromiseType,
        selectedOrderlineIds,
        campaignExperienceMedia,
        setIsLoading,
        parameters,
        showClientOrderNumberTextBasedOnCountry,
        countryCode,
        orderlinesMetaData,
        mapInitialized
    ]);

    return (
        <div className={styles.container}>
            <SubMenu
                activePage={activePage}
                setActivePage={setActivePage}
                campaignPromiseType={campaignPromiseType}
                subMenuConditions={subMenuConditions}
            />
            <div className={styles.contentWrapper}>
                {renderPage()}
            </div>
        </div>
    );
};

DeliveryReport.propTypes = {
    orderData: PropTypes.object.isRequired,
    customerId: PropTypes.string.isRequired,
    parameters: PropTypes.object.isRequired,
    campaignType: PropTypes.string.isRequired,
    activePage: PropTypes.string.isRequired,
    setActivePage: PropTypes.func.isRequired,
    campaignPromiseType: PropTypes.string.isRequired,
    orderlinesData: PropTypes.object,
    setOrderlinesData: PropTypes.func.isRequired,
    campaignName: PropTypes.string.isRequired
};

export default DeliveryReport;
